import React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";

const isReactElement = (node: unknown): node is React.ReactElement =>
  React.isValidElement(node);

const Tooltip = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.TooltipTrigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.TooltipTrigger>
>(({ children, className, content, ...props }, ref) => {
  // Check if the child is a React element and is disabled
  const shouldWrap =
    isReactElement(children) &&
    "props" in children &&
    "disabled" in children.props &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    Boolean(children.props.disabled);

  const wrappedChild = shouldWrap ? (
    <span className="inline-block" tabIndex={-1}>
      {children}
    </span>
  ) : (
    children
  );

  return (
    <TooltipPrimitive.TooltipProvider delayDuration={500} {...props}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger ref={ref} asChild {...props}>
          {wrappedChild}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            className={cn(
              "px-[15px] py-2.5 bg-[white] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] rounded",
              "text-base leading-none",
              "select-none will-change-[transform,opacity]",
              "z-50",
              className,
            )}
          >
            {content}
            <TooltipPrimitive.TooltipArrow className="fill-white" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.TooltipProvider>
  );
});
Tooltip.displayName = TooltipPrimitive.TooltipProvider.displayName;

export { Tooltip };
